<template>
  <div ref="root" class="attr-dropdown" @keydown="onKeydown">
    <LgButton size="sm" variant="ghost" :aria-pressed="open" @click="toggleOpen">
      <template #icon><Filter :size="14" aria-hidden="true" /></template>
      Fields
    </LgButton>

    <div v-if="open" class="panel" role="dialog" aria-label="Add a field filter">
      <template v-if="!picked">
        <LgInput
          v-model="keySearch"
          class="key-search"
          placeholder="Search fields…"
          aria-label="Search fields"
        >
          <template #lead><Search :size="13" aria-hidden="true" /></template>
        </LgInput>

        <!-- `message` is a first-class field (a TEXT term, not an attr) — surfaced
             first and visually set apart from the discovered attr keys. -->
        <button v-if="showMessageField" type="button" class="message-field" @click="pickMessage">
          <span class="key-name">message</span>
          <span class="key-meta">match log message text</span>
        </button>

        <div v-if="loading" class="panel-note">Loading…</div>
        <div v-else-if="error" class="panel-note err">Couldn't load attribute keys.</div>
        <div v-else-if="!filteredKeys.length && !showMessageField" class="panel-note">
          No attributes found in these logs.
        </div>
        <ul
          v-else-if="filteredKeys.length"
          class="key-list"
          role="listbox"
          aria-label="Attribute keys"
        >
          <li v-for="k in filteredKeys" :key="k.key">
            <button
              type="button"
              class="key-item"
              :class="{ 'is-active': isActive(k.key) }"
              @click="pickKey(k)"
            >
              <span class="key-name">
                {{ k.key }}
                <Check v-if="isActive(k.key)" :size="12" aria-hidden="true" class="active-check" />
              </span>
              <span class="key-meta">
                <template v-if="isActive(k.key)"
                  >filtering: {{ props.activeAttrs[k.key] }}</template
                >
                <template v-else>{{ k.count }} · {{ k.sample }}</template>
              </span>
            </button>
          </li>
        </ul>

        <p v-if="atCap" class="panel-note cap-note">
          Max {{ maxAttrFilters }} attribute filters active — remove one to add another.
        </p>
      </template>

      <template v-else>
        <button type="button" class="back-btn" @click="goBack">
          <ChevronLeft :size="14" aria-hidden="true" />
          {{ picked.kind === 'message' ? 'message' : picked.info.key }}
        </button>

        <!-- Include / exclude selector — both message and attr terms support
             negation, so a user can add an exclude directly. -->
        <div class="mode-row" role="group" aria-label="Include or exclude">
          <button
            type="button"
            class="mode-btn"
            :class="{ on: !negate }"
            :aria-pressed="!negate"
            @click="negate = false"
          >
            include =
          </button>
          <button
            type="button"
            class="mode-btn"
            :class="{ on: negate }"
            :aria-pressed="negate"
            @click="negate = true"
          >
            exclude ≠
          </button>
        </div>

        <LgInput
          v-model="valueText"
          class="value-input"
          :placeholder="valuePlaceholder"
          :aria-label="picked.kind === 'message' ? 'Message contains' : 'Attribute value contains'"
          @focus="onValueFocus"
          @keydown.enter.prevent="commitValue"
        />

        <!-- Attr-value autocomplete (never for the free-text `message` field). -->
        <template v-if="picked.kind === 'attr'">
          <div v-if="suggestLoading" class="panel-note">Loading values…</div>
          <template v-else-if="suggestions.length">
            <ul class="suggest-list" role="listbox" aria-label="Attribute values">
              <li v-for="v in suggestions" :key="v">
                <button type="button" class="suggest-item" @click="fillSuggestion(v)">
                  {{ v }}
                </button>
              </li>
            </ul>
            <p v-if="suggestTruncated" class="panel-note suggest-note">
              showing common values — type to filter on anything
            </p>
          </template>
          <div v-else-if="suggestLoaded && !suggestError" class="panel-note">
            No values found — type any substring.
          </div>
        </template>

        <p v-if="valueError" class="panel-note err" role="alert">{{ valueError }}</p>

        <LgButton
          size="sm"
          variant="primary"
          class="add-btn"
          :disabled="!valueText.trim()"
          @click="commitValue"
        >
          Add
        </LgButton>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount } from 'vue';
import { Filter, Search, ChevronLeft, Check } from 'lucide-vue-next';
import { api, type AttrKeyInfo } from '../api.js';
import {
  RANGE_MS,
  MAX_ATTR_FILTERS,
  MAX_TERM_VALUE_LEN,
  valueByteLen,
  type TimeRange,
} from '../composables/useFilters.js';
import LgButton from '../ui/LgButton.vue';
import LgInput from '../ui/LgInput.vue';

const props = withDefaults(
  defineProps<{
    containers: string[];
    stacks: string[];
    range: TimeRange;
    /** Number of attr filters already active, for the client-side cap note. */
    attrCount: number;
    /** Currently active attrs filters (key -> value substring). Every key here is always shown
     * in the dropdown, prepended and marked active, even when the latest discovery sample
     * doesn't happen to include it -- discovery is a best-effort sample of recent rows (see
     * /api/attr-keys), so a rare key that's actively being filtered on could otherwise vanish
     * from the list out from under the user while they're still using it. */
    activeAttrs?: Record<string, string>;
  }>(),
  { activeAttrs: () => ({}) },
);

/** `add` covers both fields the dropdown can produce: a free-text `message` term
 * (→ useFilters text term) or an `attr` term. Both carry `negate` (include/exclude). */
type AddPayload =
  | { field: 'message'; value: string; negate: boolean }
  | { field: 'attr'; key: string; value: string; negate: boolean };
const emit = defineEmits<{ add: [payload: AddPayload] }>();

const maxAttrFilters = MAX_ATTR_FILTERS;

type Picked = { kind: 'message' } | { kind: 'attr'; info: AttrKeyInfo };

const open = ref(false);
const loading = ref(false);
const error = ref(false);
const keys = ref<AttrKeyInfo[]>([]);
const keySearch = ref('');
const picked = ref<Picked | null>(null);
const valueText = ref('');
const negate = ref(false);
/** Inline note when the typed value exceeds the server's byte cap (see commitValue). */
const valueError = ref('');

const atCap = computed(() => props.attrCount >= maxAttrFilters);

// `message` is shown unless the field search is actively narrowing to something
// that isn't a prefix/substring of "message".
const showMessageField = computed(() => {
  const q = keySearch.value.trim().toLowerCase();
  return !q || 'message'.includes(q);
});

function isActive(key: string): boolean {
  return key in props.activeAttrs;
}

/** Discovered keys with any currently-active keys prepended (deduped) so they're always visible
 * and pickable regardless of whether this discovery sample happens to include them. */
const displayKeys = computed<AttrKeyInfo[]>(() => {
  const activeKeys = Object.keys(props.activeAttrs);
  if (activeKeys.length === 0) return keys.value;

  const byKey = new Map(keys.value.map((k) => [k.key, k]));
  const active = activeKeys.map(
    (key) => byKey.get(key) ?? { key, count: 0, sample: props.activeAttrs[key] },
  );
  const rest = keys.value.filter((k) => !isActive(k.key));
  return [...active, ...rest];
});

const filteredKeys = computed(() => {
  const q = keySearch.value.trim().toLowerCase();
  if (!q) return displayKeys.value;
  return displayKeys.value.filter((k) => k.key.toLowerCase().includes(q));
});

const valuePlaceholder = computed(() => {
  if (!picked.value) return '';
  if (picked.value.kind === 'message') return 'message contains…';
  return picked.value.info.sample ? `e.g. ${picked.value.info.sample}` : 'value contains…';
});

async function loadKeys() {
  loading.value = true;
  try {
    const to = Date.now();
    const from = to - RANGE_MS[props.range];
    const res = await api.attrKeys({
      containers: props.containers.join(','),
      stacks: props.stacks.join(','),
      from: String(from),
      to: String(to),
    });
    keys.value = res.keys;
    error.value = false;
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
  }
}

// --- Attr-value autocomplete ---------------------------------------------
const suggestions = ref<string[]>([]);
const suggestTruncated = ref(false);
const suggestLoading = ref(false);
const suggestLoaded = ref(false);
const suggestError = ref(false);
let suggestSeq = 0;
let suggestTimer: ReturnType<typeof setTimeout> | undefined;

function resetSuggestions() {
  clearTimeout(suggestTimer);
  suggestSeq++; // invalidate any in-flight fetch
  suggestions.value = [];
  suggestTruncated.value = false;
  suggestLoading.value = false;
  suggestLoaded.value = false;
  suggestError.value = false;
}

/** Fetch the distinct value list for the picked attr key, scoped to the current
 * containers/stacks/range. Debounced + seq-guarded so a stale response never
 * clobbers a newer one, and repeated focus/pick events coalesce into one fetch. */
function scheduleSuggestions() {
  if (!picked.value || picked.value.kind !== 'attr') return;
  if (suggestLoading.value || suggestLoaded.value) return; // guard: already fetched/fetching
  const key = picked.value.info.key;
  clearTimeout(suggestTimer);
  suggestTimer = setTimeout(() => {
    const seq = ++suggestSeq;
    suggestLoading.value = true;
    suggestError.value = false;
    const to = Date.now();
    const from = to - RANGE_MS[props.range];
    api
      .attrValues(key, {
        containers: props.containers.join(','),
        stacks: props.stacks.join(','),
        from: String(from),
        to: String(to),
      })
      .then((res) => {
        if (seq !== suggestSeq) return;
        suggestions.value = res.values;
        suggestTruncated.value = res.truncated;
      })
      .catch(() => {
        if (seq !== suggestSeq) return;
        suggestError.value = true;
      })
      .finally(() => {
        if (seq !== suggestSeq) return;
        suggestLoading.value = false;
        suggestLoaded.value = true;
      });
  }, 120);
}

function onValueFocus() {
  scheduleSuggestions();
}

function fillSuggestion(v: string) {
  valueText.value = v;
}

function toggleOpen() {
  open.value ? close() : openPanel();
}

function openPanel() {
  picked.value = null;
  keySearch.value = '';
  valueText.value = '';
  negate.value = false;
  valueError.value = '';
  resetSuggestions();
  open.value = true;
  loadKeys();
}

function close() {
  open.value = false;
}

function goBack() {
  picked.value = null;
  valueError.value = '';
  resetSuggestions();
}

function pickMessage() {
  picked.value = { kind: 'message' };
  valueText.value = '';
  negate.value = false;
  valueError.value = '';
  resetSuggestions();
}

function pickKey(k: AttrKeyInfo) {
  picked.value = { kind: 'attr', info: k };
  negate.value = false;
  valueError.value = '';
  // Prefill with the currently active value (if any) so re-opening an already-active filter
  // lets the user see/edit what's actually applied instead of silently clearing it.
  valueText.value = props.activeAttrs[k.key] ?? '';
  resetSuggestions();
  // Kick off the value fetch as soon as the key is chosen (the value input mounts
  // focused-adjacent); focus re-triggers are guarded so this never double-fetches.
  scheduleSuggestions();
}

function commitValue() {
  const p = picked.value;
  if (!p) return;
  const value = valueText.value.trim();
  if (!value) return;
  // Measure BYTES, not UTF-16 length: the composable (and the server) reject a
  // value over 256 UTF-8 bytes, so a multibyte value under 256 chars but over
  // 256 bytes would be accepted here yet silently dropped by addTextTerm/
  // addAttrTerm after we'd already closed. Surface a note and keep the panel
  // open instead of a silent no-op close.
  if (valueByteLen(value) > MAX_TERM_VALUE_LEN) {
    valueError.value = `Value too long (max ${String(MAX_TERM_VALUE_LEN)} bytes) — shorten it.`;
    return;
  }
  valueError.value = '';
  if (p.kind === 'message') {
    emit('add', { field: 'message', value, negate: negate.value });
  } else {
    emit('add', { field: 'attr', key: p.info.key, value, negate: negate.value });
  }
  close();
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && open.value) {
    e.preventDefault();
    close();
  }
}

const root = ref<HTMLElement | null>(null);
function onDocPointer(e: MouseEvent) {
  if (open.value && root.value && !root.value.contains(e.target as Node)) close();
}
document.addEventListener('pointerdown', onDocPointer);
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocPointer);
  clearTimeout(suggestTimer);
});
</script>

<style scoped>
.attr-dropdown {
  position: relative;
  display: inline-flex;
}

.panel {
  position: absolute;
  z-index: var(--lg-z-menu);
  top: calc(100% + var(--lg-space-1));
  left: 0;
  width: 280px;
  max-width: calc(100vw - var(--lg-space-6));
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-2);
  padding: var(--lg-space-2);
  border-radius: var(--lg-radius-md);
  border: 1px solid var(--lg-border);
  background: var(--lg-bg-raised);
  box-shadow: var(--lg-shadow-2);
}

/* On mobile the panel can't be trigger-anchored (a toolbar button near the right
   edge would push a fixed-width absolute panel off-screen, clipped by #app's
   overflow-x guard). Pin it as a viewport bottom-sheet — always fits, escapes the
   clip (position:fixed isn't contained by #app), and scrolls its own list. */
@media (max-width: 768px) {
  .panel {
    position: fixed;
    left: var(--lg-space-2);
    right: var(--lg-space-2);
    bottom: var(--lg-space-2);
    top: auto;
    width: auto;
    max-width: none;
    max-height: 75vh;
    overflow-y: auto;
  }
}

.key-search {
  flex-shrink: 0;
}

.panel-note {
  padding: var(--lg-space-2);
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-sm);
}
.panel-note.err {
  color: var(--lg-danger);
}
.cap-note {
  border-top: 1px solid var(--lg-border-faint);
  padding-top: var(--lg-space-2);
}
.suggest-note {
  padding-top: 0;
  font-size: var(--lg-fs-xs);
  font-style: italic;
}

.key-list,
.suggest-list {
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 260px;
  overflow-y: auto;
}
.suggest-list {
  max-height: 180px;
}
.key-item,
.message-field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  padding: var(--lg-space-2);
  border: none;
  border-radius: var(--lg-radius-sm);
  background: transparent;
  color: var(--lg-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.key-item:hover,
.message-field:hover {
  background: var(--lg-bg-hover);
}
.key-item.is-active {
  background: var(--lg-bg-hover);
}
/* `message` set apart from the attr keys with a divider + accent name. */
.message-field {
  border-bottom: 1px solid var(--lg-border-faint);
  border-radius: 0;
  flex-shrink: 0;
}
.message-field .key-name {
  color: var(--lg-accent);
}
.key-name {
  display: inline-flex;
  align-items: center;
  gap: var(--lg-space-1);
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
}
.active-check {
  color: var(--lg-accent);
}
.key-meta {
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-xs);
}

.suggest-item {
  display: block;
  width: 100%;
  padding: var(--lg-space-1) var(--lg-space-2);
  border: none;
  border-radius: var(--lg-radius-sm);
  background: transparent;
  color: var(--lg-text);
  font: inherit;
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.suggest-item:hover {
  background: var(--lg-bg-hover);
}

.mode-row {
  display: flex;
  gap: var(--lg-space-1);
}
.mode-btn {
  flex: 1;
  padding: var(--lg-space-1) var(--lg-space-2);
  border: 1px solid var(--lg-border-strong);
  border-radius: var(--lg-radius-sm);
  background: transparent;
  color: var(--lg-text-dim);
  font: inherit;
  font-size: var(--lg-fs-xs);
  cursor: pointer;
}
.mode-btn.on {
  border-color: var(--lg-accent);
  color: var(--lg-accent);
  background: var(--lg-bg-hover);
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--lg-space-1);
  align-self: flex-start;
  border: none;
  background: transparent;
  color: var(--lg-text-dim);
  font: inherit;
  font-family: var(--lg-font-mono);
  padding: var(--lg-space-1);
  cursor: pointer;
}
.back-btn:hover {
  color: var(--lg-text);
}
.add-btn {
  align-self: flex-end;
}
</style>
